import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SearchableSelect from '../SearchableSelect';
import weighingService from '@/services/weighing';
import { usePermissions } from '@/hooks/usePermissions';
import { PLATFORM_ROLES, OPERATIONAL_ROLES } from '@/types/permissions';

interface PeseeComplete {
  id: number;
  reference: string;
  date_heure: string;
  statut: string;
  type_pesee: 'reception' | 'expedition' | 'transfert';
  poids_brut: number;
  poids_tare: number;
  poids_net: number;
  camion?: string;
  site: number;
  created_at: string;
  updated_at: string;
  produit?: {
    id: number;
    nom: string;
  };
  produit_info?: {
    id: number;
    nom: string;
    conditionnement_info?: {
      id: number;
      nom: string;
      poids_tare?: number;
    };
  };
  // Champs optionnels pour les détails supplémentaires
  provenance?: string;
  connaissement?: string;
  numero_transfert?: string;
  sticker?: string;
  numero_lot?: string;
  observations?: string;
  conditionnement?: string;
  conditionnement_info?: {
    id: number;
    nom: string;
    poids_tare?: number;
  };
  nb_sacs_declares?: number;
  nb_sacs_sains?: number;
  nb_sacs_recond?: number;
  nb_sacs_humides?: number;
  nb_sacs_dechires?: number;
  nb_sacs_rejetes?: number;
  nb_sacs_acceptes?: number;
  poids_acceptes?: number;
  // Champs d'analyse qualité
  grainnage?: string;
  humidite?: number;
  quality?: number;
  quality_level?: string;
  // Champs de stockage
  magasin?: {
    id: number;
    nom: string;
  };
  magasin_id?: number;
  destination?: string;
  camion_info?: {
    id: number;
    immatriculation: string;
  };
  remorque_info?: {
    id: number;
    immatriculation: string;
  };
  transporteur_info?: {
    id: number;
    nom: string;
  };
  chauffeur_info?: {
    id: number;
    nom: string;
    prenom: string;
  };
  convoyeur_info?: {
    id: number;
    nom: string;
    prenom: string;
  };
  client_info?: {
    id: number;
    nom: string;
  };
  fournisseur_info?: {
    id: number;
    nom: string;
  };
  exportateur_info?: {
    id: number;
    nom: string;
  };
}

interface PeseeModificationModalProps {
  isOpen: boolean;
  pesee: PeseeComplete | null;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

const PeseeModificationModal: React.FC<PeseeModificationModalProps> = ({
  isOpen,
  pesee,
  onClose,
  onSuccess
}) => {
  const { hasAnyRole } = usePermissions();
  const [activeTab, setActiveTab] = useState('basic');
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Vérifier si l'utilisateur a les permissions pour modifier
  const canModify = hasAnyRole([
    PLATFORM_ROLES.PLATFORM_SUPER_ADMIN,
    OPERATIONAL_ROLES.SITE_SUPERVISOR,
    OPERATIONAL_ROLES.SITE_MANAGER
  ]);
  
  // États pour les données des relations
  const [trailers, setTrailers] = useState<any[]>([]);
  const [carriers, setCarriers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [exporters, setExporters] = useState<any[]>([]);
  const [conditionnements, setConditionnements] = useState<any[]>([]);
  const [magasins, setMagasins] = useState<any[]>([]);

  // États pour les données modifiées
  const [modifiedData, setModifiedData] = useState<any>({
    type_pesee: '',
    produit_id: '',
    conditionnement_id: '',
    camion_id: '',
    remorque_id: '',
    transporteur_id: '',
    chauffeur_id: '',
    convoyeur_id: '',
    client_id: '',
    fournisseur_id: '',
    exportateur_id: '',
    provenance: '',
    connaissement: '',
    numero_transfert: '',
    sticker: '',
    numero_lot: '',
    observations: '',
    poids_brut: '',
    poids_tare: '',
    poids_net: '',
    grainnage: '',
    humidite: '',
    quality: '',
    quality_level: '',
    nb_sacs_declares: '',
    nb_sacs_sains: '',
    nb_sacs_recond: '',
    nb_sacs_humides: '',
    nb_sacs_dechires: '',
    nb_sacs_rejetes: '',
    nb_sacs_acceptes: '',
    poids_acceptes: '',
    magasin_id: '',
    destination: ''
  });

  // Charger les données des relations
  const loadRelationsData = async () => {
    setLoadingRelations(true);
    try {
      // Charger les remorques - page_size élevé pour récupérer tous les résultats
      const trailersResponse = await weighingService.getRemorques({ page_size: 1000 });
      if (trailersResponse && trailersResponse.results) {
        setTrailers(trailersResponse.results);
      }

      // Charger les transporteurs - page_size élevé pour récupérer tous les résultats
      const carriersResponse = await weighingService.getTransporteurs({ page_size: 1000 });
      if (carriersResponse && carriersResponse.results) {
        setCarriers(carriersResponse.results);
      }

      // Charger les clients - page_size élevé pour récupérer tous les résultats
      const clientsResponse = await weighingService.getClients({ page_size: 1000 });
      if (clientsResponse && clientsResponse.results) {
        setClients(clientsResponse.results);
      }

      // Charger les exportateurs - page_size élevé pour récupérer tous les résultats
      const exportersResponse = await weighingService.getExportateurs({ page_size: 1000 });
      if (exportersResponse && exportersResponse.results) {
        setExporters(exportersResponse.results);
      }

      // Charger les conditionnements - page_size élevé pour récupérer tous les résultats
      const conditionnementsResponse = await weighingService.getConditionnements({ page_size: 1000 });
      if (conditionnementsResponse && conditionnementsResponse.results) {
        console.log('Conditionnements chargés:', conditionnementsResponse.results);
        setConditionnements(conditionnementsResponse.results);
      }

      // Charger les magasins - page_size élevé pour récupérer tous les résultats
      const magasinsResponse = await weighingService.getMagasins({ page_size: 1000 });
      if (magasinsResponse && magasinsResponse.results) {
        console.log('Magasins chargés:', magasinsResponse.results);
        setMagasins(magasinsResponse.results);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données de relations:', error);
    } finally {
      setLoadingRelations(false);
    }
  };

  // Initialiser les données modifiées quand la pesée change
  useEffect(() => {
    if (pesee) {
      console.log('=== DONNÉES DE LA PESÉE ===');
      console.log('Pesée complète:', pesee);
      console.log('Produit:', pesee.produit);
      console.log('Produit info:', pesee.produit_info);
      console.log('Produit ID:', pesee.produit?.id || pesee.produit_info?.id);
      console.log('Conditionnement:', pesee.conditionnement);
      console.log('Conditionnement info:', pesee.conditionnement_info);
      console.log('Conditionnement depuis produit_info:', pesee.produit_info?.conditionnement_info);
      console.log('Nb sacs déclarés:', pesee.nb_sacs_declares);
      console.log('Nb sacs sains:', pesee.nb_sacs_sains);
      console.log('Nb sacs recond:', pesee.nb_sacs_recond);
      console.log('Poids acceptés:', pesee.poids_acceptes);
      console.log('Magasin:', pesee.magasin);
      console.log('Magasin ID:', pesee.magasin?.id);
      console.log('Destination:', pesee.destination);
      console.log('========================');
      
      // Calculer les valeurs par défaut si elles n'existent pas
      const nbSacsSains = pesee.nb_sacs_sains || 0;
      const nbSacsRecond = pesee.nb_sacs_recond || 0;
      const nbSacsAcceptes = nbSacsSains + nbSacsRecond;
      
      // Calculer le poids accepté si pas défini
      const conditionnementCapacite = pesee.produit_info?.conditionnement_info?.poids_tare || 50;
      const FACTEUR_RECONDITIONNEMENT = 4;
      const poidsAcceptes = ((nbSacsSains) + (nbSacsRecond * FACTEUR_RECONDITIONNEMENT)) * conditionnementCapacite;
      
      setModifiedData({
        type_pesee: pesee.type_pesee || '',
        produit_id: pesee.produit?.id || pesee.produit_info?.id || '',
        conditionnement_id: pesee.conditionnement_info?.id || pesee.produit_info?.conditionnement_info?.id || '',
        camion_id: pesee.camion_info?.id || '',
        remorque_id: pesee.remorque_info?.id || '',
        transporteur_id: pesee.transporteur_info?.id || '',
        chauffeur_id: pesee.chauffeur_info?.id || '',
        convoyeur_id: pesee.convoyeur_info?.id || '',
        client_id: pesee.client_info?.id || '',
        fournisseur_id: pesee.fournisseur_info?.id || '',
        exportateur_id: pesee.exportateur_info?.id || '',
        provenance: pesee.provenance || '',
        connaissement: pesee.connaissement || '',
        numero_transfert: pesee.numero_transfert || '',
        sticker: pesee.sticker || '',
        numero_lot: pesee.numero_lot || '',
        observations: pesee.observations || '',
        poids_brut: pesee.poids_brut ? pesee.poids_brut.toString() : '',
        poids_tare: pesee.poids_tare ? pesee.poids_tare.toString() : '',
        poids_net: pesee.poids_net ? pesee.poids_net.toString() : '',
        grainnage: pesee.grainnage || '',
        humidite: pesee.humidite ? pesee.humidite.toString() : '',
        quality: pesee.quality ? pesee.quality.toString() : '',
        quality_level: pesee.quality_level || '',
        nb_sacs_declares: pesee.nb_sacs_declares ? pesee.nb_sacs_declares.toString() : '',
        nb_sacs_sains: nbSacsSains.toString(),
        nb_sacs_recond: nbSacsRecond.toString(),
        nb_sacs_humides: pesee.nb_sacs_humides ? pesee.nb_sacs_humides.toString() : '',
        nb_sacs_dechires: pesee.nb_sacs_dechires ? pesee.nb_sacs_dechires.toString() : '',
        nb_sacs_rejetes: pesee.nb_sacs_rejetes ? pesee.nb_sacs_rejetes.toString() : '',
        nb_sacs_acceptes: (pesee.nb_sacs_acceptes || nbSacsAcceptes).toString(),
        poids_acceptes: (pesee.poids_acceptes || poidsAcceptes).toString(),
        magasin_id: pesee.magasin?.id || pesee.magasin_id || '',
        destination: pesee.destination || ''
      });
    }
  }, [pesee]);

  // Calcul automatique du poids net
  useEffect(() => {
    if (modifiedData.poids_brut && modifiedData.poids_tare && modifiedData.poids_acceptes) {
      const poidsBrut = parseFloat(modifiedData.poids_brut) || 0;
      const poidsTare = parseFloat(modifiedData.poids_tare) || 0;
      const poidsSacs = parseFloat(modifiedData.poids_acceptes) || 0;
      
      const poidsNetCalcule = poidsBrut - (poidsTare + poidsSacs);
      
      if (modifiedData.poids_net !== poidsNetCalcule.toString()) {
        setModifiedData((prev: any) => ({ ...prev, poids_net: poidsNetCalcule.toString() }));
      }
    }
  }, [modifiedData.poids_brut, modifiedData.poids_tare, modifiedData.poids_acceptes]);

  // Calcul automatique des sacs acceptés
  useEffect(() => {
    if (modifiedData.nb_sacs_sains !== undefined && modifiedData.nb_sacs_recond !== undefined) {
      const nbSacsAcceptesCalcules = (parseInt(modifiedData.nb_sacs_sains) || 0) + (parseInt(modifiedData.nb_sacs_recond) || 0);
      if (modifiedData.nb_sacs_acceptes !== nbSacsAcceptesCalcules.toString()) {
        setModifiedData((prev: any) => ({ ...prev, nb_sacs_acceptes: nbSacsAcceptesCalcules.toString() }));
      }
    }
  }, [modifiedData.nb_sacs_sains, modifiedData.nb_sacs_recond]);

  // Calcul automatique du poids accepté
  useEffect(() => {
    if (modifiedData.nb_sacs_sains !== undefined && modifiedData.nb_sacs_recond !== undefined && modifiedData.conditionnement_id !== undefined) {
      const FACTEUR_RECONDITIONNEMENT = 4;
      const conditionnementCapacite = conditionnements.find(c => c.id.toString() === modifiedData.conditionnement_id?.toString())?.poids_tare || 50;
      const poidsAcceptesCalcules = ((parseInt(modifiedData.nb_sacs_sains) || 0) + ((parseInt(modifiedData.nb_sacs_recond) || 0) * FACTEUR_RECONDITIONNEMENT)) * conditionnementCapacite;
      
      if (modifiedData.poids_acceptes !== poidsAcceptesCalcules.toString()) {
        setModifiedData((prev: any) => ({ ...prev, poids_acceptes: poidsAcceptesCalcules.toString() }));
      }
    }
  }, [modifiedData.nb_sacs_sains, modifiedData.nb_sacs_recond, modifiedData.conditionnement_id, conditionnements]);

  // Charger les données des relations au montage
  useEffect(() => {
    if (isOpen) {
      loadRelationsData();
    }
  }, [isOpen]);

  // Gérer les changements de données
  const handleDataChange = (field: string, value: any) => {
    setModifiedData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Gérer la sauvegarde
  const handleSave = async () => {
    if (!pesee) return;
    
    setSaving(true);
    try {
      console.log('Sauvegarde des modifications pour la pesée:', pesee.id, modifiedData);
      
      // Préparer les données pour l'API (seulement les champs supportés par PeséeUpdate)
      const rawData = {
        // Informations de base
        type_pesee: modifiedData.type_pesee,
        
        // Informations provenance
        provenance: modifiedData.provenance,
        connaissement: modifiedData.connaissement,
        numero_transfert: modifiedData.numero_transfert,
        sticker: modifiedData.sticker,
        
        // Poids
        poids_brut: modifiedData.poids_brut ? parseFloat(modifiedData.poids_brut) : undefined,
        poids_tare: modifiedData.poids_tare ? parseFloat(modifiedData.poids_tare) : undefined,
        
        // Détails des sacs
        nb_sacs_declares: modifiedData.nb_sacs_declares ? parseInt(modifiedData.nb_sacs_declares) : undefined,
        nb_sacs_sains: modifiedData.nb_sacs_sains ? parseInt(modifiedData.nb_sacs_sains) : undefined,
        nb_sacs_recond: modifiedData.nb_sacs_recond ? parseInt(modifiedData.nb_sacs_recond) : undefined,
        nb_sacs_humides: modifiedData.nb_sacs_humides ? parseInt(modifiedData.nb_sacs_humides) : undefined,
        nb_sacs_dechires: modifiedData.nb_sacs_dechires ? parseInt(modifiedData.nb_sacs_dechires) : undefined,
        nb_sacs_rejetes: modifiedData.nb_sacs_rejetes ? parseInt(modifiedData.nb_sacs_rejetes) : undefined,
        nb_sacs_acceptes: modifiedData.nb_sacs_acceptes ? parseInt(modifiedData.nb_sacs_acceptes) : undefined,
        
        // Informations de stockage
        magasin: modifiedData.magasin_id ? parseInt(modifiedData.magasin_id) : undefined,
        destination: modifiedData.destination,
        numero_lot: modifiedData.numero_lot,
        observations: modifiedData.observations,
        
        // Relations (seulement celles supportées par PeséeUpdate)
        remorque: modifiedData.remorque_id ? parseInt(modifiedData.remorque_id) : undefined,
        transporteur: modifiedData.transporteur_id ? parseInt(modifiedData.transporteur_id) : undefined,
        client: modifiedData.client_id ? parseInt(modifiedData.client_id) : undefined,
        exportateur: modifiedData.exportateur_id ? parseInt(modifiedData.exportateur_id) : undefined
      };
      
      // Filtrer les valeurs vides, null, undefined et chaînes vides
      const dataToSend = Object.fromEntries(
        Object.entries(rawData).filter(([, value]) => {
          if (value === undefined || value === null || value === '') {
            return false;
          }
          if (typeof value === 'string' && value.trim() === '') {
            return false;
          }
          return true;
        })
      );
      
      console.log('Données envoyées à l\'API:', dataToSend);
      
      // Appel API pour mettre à jour la pesée
      const response = await weighingService.updatePesee(pesee.id, dataToSend);
      
      if (response.success) {
        const successMessage = 'Pesée modifiée avec succès';
        console.log(successMessage);
        
        // Appeler le callback de succès si fourni
        if (onSuccess) {
          onSuccess(successMessage);
        }
        
        onClose();
      } else {
        console.error('Erreur lors de la sauvegarde:', response.message);
        alert('Erreur lors de la sauvegarde: ' + (response.message || 'Erreur inconnue'));
      }
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Afficher les détails de l'erreur pour le débogage
      if (error.response) {
        console.error('=== ERREUR API DÉTAILLÉE ===');
        console.error('Status:', error.response.status);
        console.error('Status Text:', error.response.statusText);
        console.error('URL:', error.config?.url);
        console.error('Method:', error.config?.method);
        console.error('Headers:', error.response.headers);
        console.error('Data:', error.response.data);
        console.error('Request Data:', error.config?.data);
        console.error('================================');
        
        // Afficher les erreurs de validation si disponibles
        if (error.response.data && typeof error.response.data === 'object') {
          const errorDetails = Object.entries(error.response.data)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          alert(`Erreur de validation:\n${errorDetails}`);
        } else {
          alert('Erreur lors de la sauvegarde des modifications: ' + (error.response.data?.detail || error.message));
        }
      } else if (error.request) {
        console.error('Request:', error.request);
        alert('Erreur de connexion au serveur');
      } else {
        console.error('Error:', error.message);
        alert('Erreur lors de la sauvegarde des modifications');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !pesee) return null;

  // Si l'utilisateur n'a pas les permissions, afficher un message d'erreur
  if (!canModify) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Accès refusé
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <XMarkIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Permissions insuffisantes
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Vous n'avez pas les permissions nécessaires pour modifier cette pesée.
                <br />
                <br />
                <strong>Rôles autorisés :</strong>
                <br />
                • Super Administrateur Plateforme
                <br />
                • Superviseur de Site
                <br />
                • Gestionnaire de Site
              </p>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Modifier la pesée {pesee.reference}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Onglets */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <nav className="flex space-x-8">
            {[
              { id: 'basic', label: 'Informations de base' },
              { id: 'weighing', label: 'Informations de pesée' },
              { id: 'quality_analysis', label: 'Analyse qualité' },
              { id: 'storage', label: 'Informations de stockage' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        <div className="px-6 py-6">
          {loadingRelations && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <span className="ml-2 text-gray-600">Chargement des données...</span>
            </div>
          )}
          
          {!loadingRelations && activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de base</h4>
                <div className="text-sm text-gray-500">Étape 1/4</div>
              </div>
              
              {/* Note d'information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <div className="text-blue-600 text-sm">
                    <strong>Note :</strong> Certains champs (produit, conditionnement, véhicule, chauffeur, convoyeur, fournisseur, analyse qualité) ne peuvent pas être modifiés via cette interface car ils nécessitent des validations spéciales.
                  </div>
                </div>
              </div>
              
              {/* Référence - Non modifiable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                <input
                  type="text"
                  value={pesee.reference}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">La référence ne peut pas être modifiée</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de pesée</label>
                  <select 
                    value={modifiedData.type_pesee || ''}
                    onChange={(e) => handleDataChange('type_pesee', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="reception">Réception</option>
                    <option value="expedition">Expédition</option>
                    <option value="transfert">Transfert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Produit</label>
                  <input
                    type="text"
                    value={pesee.produit?.nom || pesee.produit_info?.nom || 'Non défini'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Le produit ne peut pas être modifié</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sac/Conditionnement</label>
                  <input
                    type="text"
                    value={pesee.produit_info?.conditionnement_info?.nom || pesee.conditionnement_info?.nom || 'Non défini'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Le conditionnement ne peut pas être modifié</p>
                </div>
              </div>

              {/* Informations véhicule */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">INFORMATIONS VÉHICULE</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Véhicule</label>
                    <input
                      type="text"
                      value={pesee.camion || pesee.camion_info?.immatriculation || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le véhicule ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remorque</label>
                    <SearchableSelect
                      options={trailers.map(t => ({ 
                        value: t.id.toString(), 
                        label: `${t.immatriculation} - ${t.marque} (Capacité: ${t.capacite || ''}kg)` 
                      }))}
                      value={modifiedData.remorque_id ? modifiedData.remorque_id.toString() : ''}
                      onChange={(value) => handleDataChange('remorque_id', value)}
                      placeholder="Sélectionner une remorque"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
                    <SearchableSelect
                      options={carriers.map(c => ({ 
                        value: c.id.toString(), 
                        label: `${c.nom} - ${c.zone || ''}` 
                      }))}
                      value={modifiedData.transporteur_id ? modifiedData.transporteur_id.toString() : ''}
                      onChange={(value) => handleDataChange('transporteur_id', value)}
                      placeholder="Sélectionner un transporteur"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chauffeur</label>
                    <input
                      type="text"
                      value={pesee.chauffeur_info ? `${pesee.chauffeur_info.nom} ${pesee.chauffeur_info.prenom}` : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le chauffeur ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Convoyeur</label>
                    <input
                      type="text"
                      value={pesee.convoyeur_info ? `${pesee.convoyeur_info.nom} ${pesee.convoyeur_info.prenom}` : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le convoyeur ne peut pas être modifié</p>
                  </div>
                </div>
              </div>

              {/* Informations client/fournisseur */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 text-sm mb-3">CLIENT / FOURNISSEUR</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Client</label>
                    <SearchableSelect
                      options={clients.map(c => ({ value: c.id.toString(), label: c.nom }))}
                      value={modifiedData.client_id ? modifiedData.client_id.toString() : ''}
                      onChange={(value) => handleDataChange('client_id', value)}
                      placeholder="Sélectionner un client"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Fournisseur</label>
                    <input
                      type="text"
                      value={pesee.fournisseur_info?.nom || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Le fournisseur ne peut pas être modifié</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Exportateur</label>
                    <SearchableSelect
                      options={exporters.map(e => ({ value: e.id.toString(), label: e.nom }))}
                      value={modifiedData.exportateur_id ? modifiedData.exportateur_id.toString() : ''}
                      onChange={(value) => handleDataChange('exportateur_id', value)}
                      placeholder="Sélectionner un exportateur"
                    />
                  </div>
                </div>
              </div>

              {/* Provenance */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-medium text-green-900 text-sm mb-3">PROVENANCE</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Provenance</label>
                    <input
                      type="text"
                      value={modifiedData.provenance || ''}
                      onChange={(e) => handleDataChange('provenance', e.target.value)}
                      placeholder="Lieu de provenance"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Connaissement</label>
                    <input
                      type="text"
                      value={modifiedData.connaissement || ''}
                      onChange={(e) => handleDataChange('connaissement', e.target.value)}
                      placeholder="Numéro de connaissement"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Transfert et Sticker */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h5 className="font-medium text-purple-900 text-sm mb-3">TRANSFERT ET STICKER</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Numéro de Transfert</label>
                    <input
                      type="text"
                      value={modifiedData.numero_transfert || ''}
                      onChange={(e) => handleDataChange('numero_transfert', e.target.value)}
                      placeholder="Numéro de transfert"
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">Sticker</label>
                    <input
                      type="text"
                      value={modifiedData.sticker || ''}
                      onChange={(e) => handleDataChange('sticker', e.target.value)}
                      placeholder="Numéro de sticker"
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && activeTab === 'weighing' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de pesée</h4>
                <div className="text-sm text-gray-500">Étape 2/4</div>
              </div>

              {/* Première pesée */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 text-sm mb-3">PREMIÈRE PESÉE (À CHARGE)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Poids brut (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_brut || ''}
                      onChange={(e) => handleDataChange('poids_brut', e.target.value)}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Heure d'entrée</label>
                    <input
                      type="datetime-local"
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Deuxième pesée */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h5 className="font-medium text-green-900 text-sm mb-3">DEUXIÈME PESÉE (À VIDE)</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Poids tare (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_tare || ''}
                      onChange={(e) => handleDataChange('poids_tare', e.target.value)}
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">Heure de sortie</label>
                    <input
                      type="datetime-local"
                      className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Détails des sacs */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">DÉTAILS DES SACS</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déclarés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_declares || ''}
                      onChange={(e) => handleDataChange('nb_sacs_declares', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs sains</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_sains || ''}
                      onChange={(e) => handleDataChange('nb_sacs_sains', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Recond</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_recond || ''}
                      onChange={(e) => handleDataChange('nb_sacs_recond', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs Humides</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_humides || ''}
                      onChange={(e) => handleDataChange('nb_sacs_humides', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs déchirés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_dechires || ''}
                      onChange={(e) => handleDataChange('nb_sacs_dechires', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nb de sacs rejetés</label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_rejetes || ''}
                      onChange={(e) => handleDataChange('nb_sacs_rejetes', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nb de sacs acceptés 
                      <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
                    </label>
                    <input
                      type="number"
                      value={modifiedData.nb_sacs_acceptes || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      = {modifiedData.nb_sacs_sains || 0} sains + {modifiedData.nb_sacs_recond || 0} recond 
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Poids accepté (kg)
                      <span className="text-xs text-gray-500 ml-1">(calculé automatiquement)</span>
                    </label>
                    <input
                      type="number"
                      value={modifiedData.poids_acceptes || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      = ({modifiedData.nb_sacs_sains || 0} sains + ({modifiedData.nb_sacs_recond || 0} recond × 4)) sacs × {conditionnements.find(c => c.id.toString() === modifiedData.conditionnement_id?.toString())?.poids_tare || 50} kg/sac
                    </p>
                  </div>
                </div>
              </div>

              {/* Calcul automatique du poids net */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h5 className="font-medium text-orange-900 text-sm mb-3">CALCUL AUTOMATIQUE</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">Poids sacs acceptés (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_acceptes || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg bg-orange-50 text-orange-700"
                      placeholder="Calculé automatiquement"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">Poids net (kg)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={modifiedData.poids_net || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-orange-300 rounded-lg bg-orange-50 text-orange-700 font-semibold"
                      placeholder="Calculé automatiquement"
                    />
                  </div>
                  <div className="flex items-end">
                    <div className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                      Poids Net = Poids Brut - (Poids Tare + Poids Sacs)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && activeTab === 'quality_analysis' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Analyse qualité</h4>
                <div className="text-sm text-gray-500">Étape 3/4</div>
              </div>

              {/* Note d'information */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="text-yellow-800 text-sm">
                    <strong>Information :</strong> Les champs d'analyse qualité ne peuvent pas être modifiés via cette interface car ils nécessitent des validations spéciales et des calculs automatiques. Ces informations sont généralement saisies lors du processus de pesée initial.
                  </div>
                </div>
              </div>

              {/* Affichage en lecture seule des données existantes */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 text-sm mb-3">DONNÉES ACTUELLES</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grainnage</label>
                    <input
                      type="text"
                      value={pesee.grainnage || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Humidité (%)</label>
                    <input
                      type="text"
                      value={pesee.humidite ? pesee.humidite.toString() : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualité</label>
                    <input
                      type="text"
                      value={pesee.quality ? pesee.quality.toString() : 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau de qualité</label>
                    <input
                      type="text"
                      value={pesee.quality_level || 'Non défini'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loadingRelations && activeTab === 'storage' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900">Informations de stockage</h4>
                <div className="text-sm text-gray-500">Étape 4/4</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Magasin</label>
                  <select
                    value={modifiedData.magasin_id || ''}
                    onChange={(e) => handleDataChange('magasin_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Sélectionner un magasin</option>
                    {magasins.map(magasin => (
                      <option key={magasin.id} value={magasin.id}>
                        {magasin.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
                  <input
                    type="text"
                    value={modifiedData.destination || ''}
                    onChange={(e) => handleDataChange('destination', e.target.value)}
                    placeholder="Lieu de destination"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de lot</label>
                  <input
                    type="text"
                    value={modifiedData.numero_lot || ''}
                    onChange={(e) => handleDataChange('numero_lot', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observations</label>
                  <textarea
                    rows={3}
                    value={modifiedData.observations || ''}
                    onChange={(e) => handleDataChange('observations', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {activeTab === 'basic' && 'Informations de base'}
            {activeTab === 'weighing' && 'Informations de pesée'}
            {activeTab === 'quality_analysis' && 'Analyse qualité'}
            {activeTab === 'storage' && 'Informations de stockage'}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loadingRelations}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeseeModificationModal;
